import {
  getLayoutRequestTransferList,
  packLayoutSnapshot,
  validateLayoutResponse,
  type LayoutErrorCode,
  type PackedLayoutSnapshot,
  type LayoutResult,
  type LayoutSnapshot,
  type LayoutWorkerRequest,
  type LayoutWorkerResponse,
} from './LayoutProtocol.js'
import {
  LayoutCanvasUnsupportedError,
  createCanvasTextRasterizer,
  scanLayout,
  type LayoutCanvasFactory,
  type LayoutCanvasSurface,
} from './scanLayout.js'

export interface LayoutWorkerEvent {
  readonly data?: unknown
  readonly error?: unknown
  readonly message?: string
  preventDefault?(): void
}

export interface LayoutWorkerLike {
  postMessage(message: LayoutWorkerRequest, transfer: readonly Transferable[]): void
  addEventListener(
    type: 'message' | 'error' | 'messageerror',
    listener: (event: LayoutWorkerEvent) => void,
  ): void
  removeEventListener(
    type: 'message' | 'error' | 'messageerror',
    listener: (event: LayoutWorkerEvent) => void,
  ): void
  terminate(): void
}

export interface LayoutWorkerClientOptions {
  readonly createWorker?: () => LayoutWorkerLike
  readonly fallback?: (snapshot: LayoutSnapshot) => Promise<LayoutResult>
  readonly fallbackCanvasFactory?: LayoutCanvasFactory
}

export class LayoutCancelledError extends Error {
  constructor() {
    super('Layout request was superseded or cancelled')
    this.name = 'LayoutCancelledError'
  }
}

export class LayoutDisposedError extends Error {
  constructor() {
    super('Layout worker client has been disposed')
    this.name = 'LayoutDisposedError'
  }
}

export class LayoutWorkerError extends Error {
  readonly code: LayoutErrorCode

  constructor(code: LayoutErrorCode, message: string) {
    super(message)
    this.name = 'LayoutWorkerError'
    this.code = code
  }
}

interface PendingLayout {
  readonly revision: number
  readonly snapshot: LayoutSnapshot
  readonly particleBudget: number
  readonly resolve: (result: LayoutResult) => void
  readonly reject: (error: unknown) => void
  fallbackStarted: boolean
}

function unpackOffsets(offsets: Float32Array): ReadonlyArray<Readonly<{ x: number; y: number }>> {
  const unpacked = new Array<{ x: number; y: number }>(offsets.length / 2)
  for (let index = 0; index < unpacked.length; index += 1) {
    unpacked[index] = {
      x: offsets[index * 2] ?? 0,
      y: offsets[index * 2 + 1] ?? 0,
    }
  }
  return unpacked
}

function fallbackSnapshotFromPacked(snapshot: PackedLayoutSnapshot): LayoutSnapshot {
  return {
    seed: snapshot.seed,
    text: snapshot.text,
    fontName: snapshot.fontName,
    ...(snapshot.fontSource === undefined ? {} : { fontSource: snapshot.fontSource }),
    fontSize: snapshot.fontSize,
    lineHeight: snapshot.lineHeight,
    wordSpacing: snapshot.wordSpacing,
    textAlign: snapshot.textAlign,
    baseline: snapshot.baseline,
    density: snapshot.density,
    gridMode: snapshot.gridMode,
    width: snapshot.width,
    height: snapshot.height,
    particleBudget: snapshot.particleBudget,
    wordOffsets: unpackOffsets(snapshot.wordOffsets),
    letterOffsets: unpackOffsets(snapshot.letterOffsets),
    dotOffsets: unpackOffsets(snapshot.dotOffsets),
  }
}

function createDocumentCanvas(width: number, height: number): LayoutCanvasSurface {
  const documentObject = (globalThis as unknown as {
    readonly document?: { createElement(name: 'canvas'): { width: number; height: number } }
  }).document
  if (documentObject === undefined) {
    throw new LayoutCanvasUnsupportedError('Main-thread Canvas2D is unavailable')
  }
  const canvas = documentObject.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas as unknown as LayoutCanvasSurface
}

export function createMainThreadLayoutFallback(
  createCanvas: LayoutCanvasFactory = createDocumentCanvas,
): (snapshot: LayoutSnapshot) => Promise<LayoutResult> {
  const rasterizer = createCanvasTextRasterizer(createCanvas)
  return (snapshot) => {
    try {
      return scanLayout(packLayoutSnapshot(snapshot), rasterizer)
    } catch (error) {
      return Promise.reject(
        error instanceof Error ? error : new Error('Canvas layout fallback failed'),
      )
    }
  }
}

function responseHeader(value: unknown): { type: string; revision: number } | null {
  if (typeof value !== 'object' || value === null) return null
  const record = value as Record<string, unknown>
  if (typeof record.type !== 'string') return null
  if (typeof record.revision !== 'number' || !Number.isSafeInteger(record.revision) || record.revision < 0) {
    return null
  }
  return { type: record.type, revision: record.revision }
}

export class LayoutWorkerClient {
  private readonly fallback: (snapshot: LayoutSnapshot) => Promise<LayoutResult>
  private readonly worker: LayoutWorkerLike | null
  private readonly onMessage = (event: LayoutWorkerEvent): void => {
    this.handleMessage(event.data)
  }
  private readonly onWorkerFailure = (event: LayoutWorkerEvent): void => {
    event.preventDefault?.()
    this.disableWorker()
    if (this.pending !== null && !this.disposed) this.startFallback(this.pending)
  }

  private revision = 0
  private pending: PendingLayout | null = null
  private disposed = false
  private terminated = false

  constructor(options: LayoutWorkerClientOptions = {}) {
    this.fallback = options.fallback
      ?? createMainThreadLayoutFallback(options.fallbackCanvasFactory)
    let worker: LayoutWorkerLike | null = null
    if (options.createWorker !== undefined) {
      try {
        worker = options.createWorker()
      } catch {
        worker = null
      }
    }
    this.worker = worker
    if (this.worker !== null) {
      try {
        this.worker.addEventListener('message', this.onMessage)
        this.worker.addEventListener('error', this.onWorkerFailure)
        this.worker.addEventListener('messageerror', this.onWorkerFailure)
      } catch {
        this.disableWorker()
      }
    }
  }

  layout(snapshot: LayoutSnapshot): Promise<LayoutResult> {
    if (this.disposed) return Promise.reject(new LayoutDisposedError())
    let packed: ReturnType<typeof packLayoutSnapshot>
    let fallbackSnapshot: LayoutSnapshot
    let revision: number
    try {
      packed = packLayoutSnapshot(snapshot)
      fallbackSnapshot = fallbackSnapshotFromPacked(packed)
      revision = this.nextRevision()
    } catch (error) {
      return Promise.reject(
        error instanceof Error
          ? error
          : new Error('Layout request preparation failed'),
      )
    }
    if (this.pending !== null) {
      const previous = this.pending
      this.pending = null
      previous.reject(new LayoutCancelledError())
    }
    this.revision = revision
    return new Promise<LayoutResult>((resolve, reject) => {
      const pending: PendingLayout = {
        revision,
        snapshot: fallbackSnapshot,
        particleBudget: packed.particleBudget,
        resolve,
        reject,
        fallbackStarted: false,
      }
      this.pending = pending
      if (this.worker === null || this.terminated) {
        this.startFallback(pending)
        return
      }
      const request: LayoutWorkerRequest = { type: 'layout', revision, snapshot: packed }
      try {
        this.worker.postMessage(request, getLayoutRequestTransferList(request))
      } catch {
        this.disableWorker()
        this.startFallback(pending)
      }
    })
  }

  cancelCurrent(): void {
    if (this.disposed) return
    if (this.pending !== null) {
      const pending = this.pending
      this.pending = null
      pending.reject(new LayoutCancelledError())
    }
    this.revision = this.nextRevision()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    if (this.pending !== null) {
      const pending = this.pending
      this.pending = null
      pending.reject(new LayoutDisposedError())
    }
    this.disableWorker()
  }

  private nextRevision(): number {
    if (this.revision >= Number.MAX_SAFE_INTEGER) {
      throw new RangeError('Layout revision exhausted the safe-integer range')
    }
    return this.revision + 1
  }

  private disableWorker(): void {
    if (this.worker === null || this.terminated) return
    this.terminated = true
    for (const [type, listener] of [
      ['message', this.onMessage],
      ['error', this.onWorkerFailure],
      ['messageerror', this.onWorkerFailure],
    ] as const) {
      try {
        this.worker.removeEventListener(type, listener)
      } catch {
        // A failed worker may already have torn down its event target.
      }
    }
    try {
      this.worker.terminate()
    } catch {
      // Termination is best-effort after a worker runtime failure.
    }
  }

  private startFallback(pending: PendingLayout): void {
    if (pending.fallbackStarted) return
    pending.fallbackStarted = true
    Promise.resolve()
      .then(() => this.fallback(pending.snapshot))
      .then((result) => {
        if (this.pending !== pending || this.disposed) return
        const response: LayoutWorkerResponse = {
          type: 'layout-result',
          revision: pending.revision,
          result,
        }
        validateLayoutResponse(response, pending.particleBudget)
        this.pending = null
        pending.resolve(result)
      })
      .catch((error: unknown) => {
        if (this.pending !== pending || this.disposed) return
        this.pending = null
        pending.reject(error)
      })
  }

  private handleMessage(value: unknown): void {
    const header = responseHeader(value)
    const pending = this.pending
    if (header === null || pending === null || header.revision !== pending.revision) return
    if (pending.fallbackStarted) return
    try {
      validateLayoutResponse(value, pending.particleBudget)
    } catch {
      // Malformed current messages are ignored without changing pending state;
      // a later valid response for the same revision may still complete it.
      return
    }
    const response = value
    if (response.type === 'layout-result') {
      this.pending = null
      pending.resolve(response.result)
      return
    }
    if (response.code === 'unsupported') {
      this.disableWorker()
      this.startFallback(pending)
      return
    }
    this.pending = null
    pending.reject(new LayoutWorkerError(response.code, response.message))
  }
}
